import { useNavigate } from '@tanstack/react-router';
import { Monitor, Smartphone, ArrowRight, Clock, FileText, BookOpen } from 'lucide-react';
import { TUTORIAL_PRODUCTS, type TutorialProduct } from '@/data/tutorials';

const PRODUCT_META: Record<string, { icon: React.ElementType; color: string; bg: string }> = {
  'dashboard': { icon: Monitor, color: 'text-brand-blue', bg: 'bg-brand-blue/10' },
  'app-mobile': { icon: Smartphone, color: 'text-[#7c3aed]', bg: 'bg-[#7c3aed]/10' },
};

function ProductCard({ product }: { product: TutorialProduct }) {
  const navigate = useNavigate();
  const meta = PRODUCT_META[product.id];
  const Icon = meta.icon;

  const totalArticles = product.categories.reduce((sum, c) => sum + c.articles.length, 0);
  const firstCategory = product.categories[0];
  const firstArticle = firstCategory?.articles[0];

  function handleClick() {
    if (firstCategory && firstArticle) {
      navigate({
        to: '/tutorials/$productId/$categoryId/$articleId',
        params: {
          productId: product.id,
          categoryId: firstCategory.id,
          articleId: firstArticle.id,
        },
      });
    }
  }

  return (
    <button
      onClick={handleClick}
      className="group w-full rounded-3xl bg-brand-white p-8 shadow-xl transition-all hover:scale-[1.02] hover:shadow-2xl text-left"
    >
      <div className={`mb-5 inline-flex h-16 w-16 items-center justify-center rounded-2xl ${meta.bg}`}>
        <Icon className={`h-8 w-8 ${meta.color}`} />
      </div>

      <div className="mb-2 flex items-start justify-between gap-4">
        <h2 className="text-2xl font-extrabold text-brand-black">{product.title}</h2>
        <ArrowRight className={`mt-1 h-5 w-5 flex-shrink-0 ${meta.color} transition-transform group-hover:translate-x-1`} />
      </div>

      <p className="mb-1 text-sm font-bold text-brand-black/50 uppercase tracking-wide">
        {product.subtitle}
      </p>

      <p className="mb-6 text-base font-medium text-brand-black/70 leading-relaxed">
        {product.description}
      </p>

      <div className="flex flex-wrap gap-4">
        <div className="flex items-center gap-1.5 text-sm font-medium text-brand-black/60">
          <FileText className="h-4 w-4" />
          <span>{product.categories.length} topics</span>
        </div>
        <div className="flex items-center gap-1.5 text-sm font-medium text-brand-black/60">
          <BookOpen className="h-4 w-4" />
          <span>{totalArticles} guides</span>
        </div>
        <div className="flex items-center gap-1.5 text-sm font-medium text-brand-black/60">
          <Clock className="h-4 w-4" />
          <span>{product.audience}</span>
        </div>
      </div>
    </button>
  );
}

export default function TutorialsLandingPage() {
  return (
    <div className="flex flex-col bg-brand-yellow">
      {/* Hero */}
      <section className="relative overflow-hidden bg-gradient-to-br from-brand-yellow via-brand-yellow to-[#FFE566] py-16">
        <div
          className="absolute inset-0 opacity-10"
          style={{
            backgroundImage: 'radial-gradient(circle, #004CFF 1px, transparent 1px)',
            backgroundSize: '50px 50px',
          }}
        />
        <div className="container relative z-10 text-center">
          <h1 className="mb-4 text-5xl font-extrabold tracking-tight text-brand-black md:text-6xl">
            UNILinkd Tutorials
          </h1>
          <p className="mx-auto max-w-2xl text-xl font-medium text-brand-black/80">
            Step-by-step guides to help you and your institution get the most out of UNILinkd — from day one.
          </p>
        </div>
      </section>

      {/* Product Cards */}
      <section className="bg-brand-white py-16">
        <div className="container">
          <div className="mb-10 text-center">
            <h2 className="text-3xl font-extrabold text-brand-black">Choose your product</h2>
            <p className="mt-2 text-base font-medium text-brand-black/60">
              Guides are organized by product and audience.
            </p>
          </div>

          <div className="grid gap-6 md:grid-cols-3">
            {TUTORIAL_PRODUCTS.map((product) => (
              <ProductCard key={product.id} product={product} />
            ))}
          </div>
        </div>
      </section>

      {/* Quick Tips Banner */}
      <section className="bg-brand-yellow py-12">
        <div className="container">
          <div className="rounded-3xl bg-brand-white p-8 shadow-xl">
            <h3 className="mb-4 text-xl font-extrabold text-brand-black">Tips for using these guides</h3>
            <ul className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
              {[
                'Follow steps in order — each step builds on the previous one.',
                'Image placeholders show where screenshots will appear.',
                'Each guide shows estimated reading time.',
                'Use the sidebar to jump to any topic or article.',
                'Related articles at the bottom link to connected topics.',
                'Contact your UNILinkd representative if you need further help.',
              ].map((tip, i) => (
                <li key={i} className="flex items-start gap-3">
                  <span className="mt-0.5 flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full bg-brand-blue text-[10px] font-bold text-white">
                    {i + 1}
                  </span>
                  <span className="text-sm font-medium text-brand-black/70">{tip}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>
    </div>
  );
}
